import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useState } from "react";

interface DatePickerProps {
  field: {
    value: Dayjs | null;
    onChange: (date: Dayjs | null) => void;
    onBlur?: () => void;
  };
  label?: string;
  format?: string;
  minDate?: string | Dayjs;
  maxDate?: string | Dayjs;
  disableFuture?: boolean;
  disablePast?: boolean;
  shouldDisableDate?: (date: Dayjs) => boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  trigger?: any;
  slotProps?: any;
  [key: string]: any;
}

const DateInput = ({
  field,
  label,
  format = "DD MMM, YYYY",
  minDate,
  maxDate,
  disableFuture,
  disablePast,
  shouldDisableDate,
  error,
  helperText,
  required,
  trigger,
  slotProps,
  ...rest
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        {...rest}
        open={open}
        onOpen={() => setOpen(true)}
        label={label}
        format={format}
        value={field.value ? dayjs(field.value) : null}
        {...(minDate && { minDate: dayjs(minDate) })}
        {...(maxDate && { maxDate: dayjs(maxDate) })}
        {...(disableFuture && { disableFuture })}
        {...(disablePast && { disablePast })}
        {...(shouldDisableDate && { shouldDisableDate })}
        onChange={(newValue) => {
          field.onChange(newValue ?? null);
        }}
        onClose={() => {
          setOpen(false);
          field.onBlur?.();
          trigger?.();
        }}
        slotProps={{
          textField: {
            variant: "standard",
            fullWidth: true,
            error,
            required,
            helperText,
            onClick: () => setOpen(true),
            inputProps: {
              readOnly: true,
            },
            FormHelperTextProps: {
              sx: {
                position: "absolute",
                top: "100%",
                left: 0,
                margin: 0,
                fontSize: "10px",
                lineHeight: "12px",
                whiteSpace: "nowrap",
              },
            },
            sx: {
              position: "relative",
              "& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersInput-root": {
                fontSize: "10.5px",
              },
              "& .MuiInputBase-input, & .MuiPickersInputBase-input": {
                cursor: "pointer",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: error ? "#d32f2f" : "#6b7280",
              },
              "& *:before": {
                borderBottom: error ? "1px solid #d32f2f !important" : "1px solid rgba(0, 0, 0, 0.42) !important",
              },
              "& *:hover:before": {
                borderBottom: error ? "1px solid #d32f2f !important" : "1px solid rgba(0, 0, 0, 0.42) !important",
              },
              "& *:after": {
                borderBottomColor: error ? "#d32f2f" : "#5145e5",
              },
            },
            ...slotProps?.textField,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DateInput;
